import { Directive, EventEmitter, Injectable, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Bonocliente } from '../../models/bonocliente';
import { DataCombo } from '../../models/combo';
import { Evento, EventServicioDetalle, EventProductoDetalle } from '../../models/evento';
import { SettingValue } from '../../models/setting';
import { Ticket } from '../../models/ticket';
import { SettingService } from '../../services/setting.service';
import { TicketService } from '../../services/ticket.service';
import { UsuarioService } from '../../services/usuario.service';
import { UtilsService } from '../../services/utils.service';
import { StorageService } from '../../services/storage.service';

export interface ResumenLinea {
  tipo: string;
  detalle: string;
}

@Directive()
@Injectable()
export abstract class AbstractFormDetalleTicket implements OnInit {

  @Input() cita: Evento;
  @Input() ticketId: number = null;

  @Output() sendToastMsg: EventEmitter<object> = new EventEmitter<object>();
  @Output() modificarCita = new EventEmitter<Evento>();
  @Output() cancel = new EventEmitter<void>();

  public tForm: FormGroup;
  public usuariosCombo: DataCombo[];
  public totalPagar: number;
  public importeFinanciado: number = 0;
  public tiendaId: number;
  public clienteId: number;
  public userId: number;
  public iva: number;
  public ticket: Ticket;
  bcAplicablesTk: Bonocliente[];
  bcServicios: Bonocliente[];
  bcEconomicos: Bonocliente[];
  descuentoEconomico: number = 0;
  bonosPorServicio: { [servicioId: number]: Bonocliente[] } = {};

  constructor(
    protected utilsService: UtilsService,
    protected storageService: StorageService,
    protected settingService: SettingService,
    protected usuarioService: UsuarioService,
    protected ticketService: TicketService,
    protected fb: FormBuilder
  ) { }

  protected abstract configurarComponenteEspecifico(): void;

  ngOnInit(): void {
    this.userId = this.storageService.getEntityUser();
    this.tiendaId = this.utilsService.getCurrentTiendaId();
    if (this.ticketId) {
      this.getTicket();
    } else {
      this.clienteId = this.cita.clienteId;
      this.prepararForm();
    }
  }

  getTicket() {
    this.ticketService.getById(this.ticketId).subscribe(
      (data) => {
        this.ticket = data;
        this.cita = this.ticket.event;
        this.clienteId = this.cita.clienteId;
        this.prepararForm();
      },
      (error) => {
        var errorMessage = <any>error;
        if (errorMessage != null) {
          this.sendToastMsg.emit({
            mensajeError: true,
            mensaje: error.error.message,
          });
        }
      }
    );
  }

  async prepararForm() {
    await Promise.all([
      this.getUsuarios().toPromise(),
      this.getBonoclientesAptosParaTicket().toPromise(),
      this.getIva().toPromise()
    ]);

    this.buildForm();
    this.calcularTotalesBase();
    this.calcularImporteTotal();
    this.configurarComponenteEspecifico();
  }

  get eventServicioDetalles(): FormArray {
    return this.tForm?.get('eventServicioDetalles') as FormArray;
  }

  get eventProductoDetalles(): FormArray {
    return this.tForm?.get('eventProductoDetalles') as FormArray;
  }

  get bonosEconomicos(): FormArray {
    return this.tForm?.get('bonosEconomicos') as FormArray;
  }

  actualizarBonosParaServicio(servicioId: number) {
    this.bonosPorServicio[servicioId] = this.bcServicios
      .filter(bc => bc.servicioIds?.includes(servicioId))
      .map(bc => ({
        ...bc,
        label: `${bc.codigo} - ${bc.bono?.baseBono?.name}`
      }));
  }

  cantidadDelServicio(nombre: string): number {
    return this.eventServicioDetalles.controls.filter(g => g.value.nombre === nombre).length;
  }

  getNombreBono(id: number): string {
    const bono =
      this.bcEconomicos.find(b => b.id === id) ??
      this.bcServicios.find(b => b.id === id);

    if (!bono) return 'Bono desconocido';

    const codigo = bono.codigo?.toString().padStart(6, '0');
    const nombre = bono.bono?.baseBono?.name ?? 'Sin nombre';

    return `${codigo} - ${nombre}`;
  }

  getSaldoBono(id: number): number {
    const bono = this.bcEconomicos.find(b => b.id === id) ?? this.bcServicios.find(b => b.id === id);
    return bono?.saldo ?? 0;
  }

  getCodigoBono(id: number): string {
    const bono = this.bcEconomicos.find(b => b.id === id) ?? this.bcServicios.find(b => b.id === id);
    return bono?.codigo?.toString() ?? 'Desconocido';
  }

  get resumenDescuentos(): ResumenLinea[] {
    const resumen: ResumenLinea[] = [];

    // Bonos económicos
    this.bonosEconomicos.controls.forEach(grupo => {
      const id = grupo.get('bonoclienteId')?.value;
      const importe = grupo.get('importeADescontar')?.value;
      if (id && importe && importe > 0) {
        const nombre = this.getNombreBono(id);
        resumen.push({
          tipo: 'Descuento bono',
          detalle: `${nombre} - ${Number(importe).toFixed(2)} €`
        });
      }
    });

    // Bonos por servicio
    this.eventServicioDetalles.controls.forEach(grupo => {
      const id = grupo.get('bonoclienteId')?.value;
      const descuento = grupo.get('descuentoBono')?.value;
      if (id && descuento && descuento > 0) {
        const nombre = this.getNombreBono(id);
        resumen.push({
          tipo: 'Descuento bono',
          detalle: `${nombre} - ${Number(descuento).toFixed(2)} €`
        });
      }
    });

    // Financiaciones
    this.eventServicioDetalles.controls.forEach(grupo => {
      const financiado = grupo.get('financiado')?.value;
      const nombre = grupo.get('nombre')?.value;
      const precioPlazo = grupo.get('precioPlazo')?.value;
      if (financiado && precioPlazo > 0) {
        resumen.push({
          tipo: 'Importe financiado',
          detalle: `${nombre} - ${Number(precioPlazo).toFixed(2)} €`
        });
      }
    });
    return resumen;
  }

  private buildForm(): void {
    this.tForm = this.fb.group({
      dia: [this.cita.startsAt ?? null],
      description: [this.cita.description ?? null],
      totalSinIva: [this.ticket?.totalSinIva ?? null],
      totalIva: [this.ticket?.totalIva ?? null],
      ivaAplicado: [this.ticket?.ivaAplicado ? this.ticket.ivaAplicado * 100 : this.iva ?? 21],
      total: [this.ticket?.total ?? null],
      info: [this.ticket?.info ?? null],
      importeADescontar: [0],
      bonosEconomicos: this.fb.array(this.getBonosEconomicosGroups()),
      eventServicioDetalles: this.fb.array(this.getServicioDetalleGroups()),
      eventProductoDetalles: this.fb.array(this.getProductoDetalleGroups()),
      ...this.utilsService.getCamposComunesFormPago(this.ticket ?? null)
    });
  }

  private getBonosEconomicosGroups(): FormGroup[] {
    const grupos: FormGroup[] = [];

    if (this.ticket?.bonomoves) {
      // Filtrar bonomoves que corresponden a bonos económicos (sin servicioTipo)
      const bonomovesEconomicos = this.ticket.bonomoves.filter(bm =>
        bm.bonocliente?.bono?.baseBono?.servicioTipo === null
      );

      bonomovesEconomicos.forEach(bonomove => {
        // Verificar que el bonocliente este en la lista de bonos economicos disponibles
        const bonoclienteEnLista = this.bcEconomicos?.find(bc => bc.id === bonomove.bonocliente?.id);

        if (bonoclienteEnLista) {
          const grupo = this.fb.group({
            bonoclienteId: [bonomove.bonocliente.id],
            importeADescontar: [bonomove.quantity || 0, [
              Validators.min(0),
              Validators.max(bonoclienteEnLista.saldo)
            ]]
          });

          // Suscribir a cambios para recalcular total
          grupo.get('importeADescontar')?.valueChanges.subscribe(() => {
            this.calcularImporteTotal();
          });

          grupos.push(grupo);
        }
      });
    }

    return grupos;
  }

  private calcularTotalesBase(): void {
    let totalBase = 0;

    this.eventServicioDetalles.controls.forEach(grupo => {
      const precio = Number(grupo.get('precio')?.value ?? 0);
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      totalBase += precio * cantidad;
    });

    this.eventProductoDetalles.controls.forEach(grupo => {
      const precio = Number(grupo.get('precio')?.value ?? 0);
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      totalBase += precio * cantidad;
    });

    const ivaPorcentaje = Number(this.tForm.get('ivaAplicado')?.value ?? 21);
    const ivaDecimal = ivaPorcentaje / 100;

    const totalConIva = +totalBase.toFixed(2);
    const totalIva = +(totalConIva * ivaDecimal / (1 + ivaDecimal)).toFixed(2);
    const totalSinIva = +(totalConIva - totalIva).toFixed(2);

    this.tForm.patchValue({
      total: totalConIva,
      totalIva,
      totalSinIva
    });
  }

  public calcularImporteTotal(): void {
    let totalFinal = 0;
    let importeFinanciadoPendiente = 0;

    this.eventServicioDetalles.controls.forEach(grupo => {
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      const financiado = grupo.get('financiado')?.value;
      const precio = Number(grupo.get('precio')?.value ?? 0);
      const precioPlazo = Number(grupo.get('precioPlazo')?.value ?? 0);
      const numPlazos = Number(grupo.get('numPlazos')?.value ?? 1);
      const descuentoBono = Number(grupo.get('descuentoBono')?.value ?? 0);

      let subtotal = precio * cantidad;
      subtotal -= descuentoBono;
      totalFinal += subtotal;

      if (financiado && numPlazos > 1) {
        const pendiente = precioPlazo * (numPlazos - 1) * cantidad;
        importeFinanciadoPendiente += pendiente;
      }
    });

    this.eventProductoDetalles.controls.forEach(grupo => {
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      const precio = Number(grupo.get('precio')?.value ?? 0);
      totalFinal += precio * cantidad;
    });

    const descuentosPorBonos = this.bonosEconomicos.controls.reduce((acc, grupo) => {
      return acc + Number(grupo.get('importeADescontar')?.value ?? 0);
    }, 0);

    const descuentoManual = Number(this.tForm.get('importeADescontar')?.value ?? 0);

    totalFinal -= (descuentosPorBonos + descuentoManual);
    totalFinal -= importeFinanciadoPendiente;

    this.totalPagar = Math.max(0, +totalFinal.toFixed(2));
    this.importeFinanciado = +importeFinanciadoPendiente.toFixed(2);
  }

  private getServicioDetalleGroups(): FormGroup[] {
    const grupos: FormGroup[] = [];
    if (this.ticket) {
      // Map para llevar la cuenta de cuántas veces se ha usado cada bonomove (por id)
      const bonomoveUsos: { [id: number]: number } = {};

      this.ticket.event?.eventServicios.forEach(eventServicio => {
        const cantidad = eventServicio.cantidad ?? 1;
        this.actualizarBonosParaServicio(eventServicio.servicio?.id);

        for (let i = 0; i < cantidad; i++) {
          const detalleOriginal = eventServicio.eventServicioDetalles?.[i];
          const detalle = detalleOriginal ?? new EventServicioDetalle();

          // Buscar el bonomove que coincida con este servicio y que tenga usos disponibles
          let bonomoveAplicable = null;
          let bonoclienteId = null;
          let descuentoBono = 0;
          let bonoCodigoManual = '';
          let modoBono = 'selector';

          if (detalleOriginal && this.ticket.bonomoves) {
            // Buscar bonomove con baseServicio.id igual y con usos disponibles
            bonomoveAplicable = this.ticket.bonomoves.find(bm => {
              const baseServicioId = bm.bonocliente?.bono?.baseBono?.baseServicio?.id;
              const coincideServicio = baseServicioId === eventServicio.servicio?.baseServicio?.id;
              if (!coincideServicio) return false;
              // Cuántas veces se ha usado este bonomove
              const usados = bonomoveUsos[bm.id] || 0;
              return usados < (bm.quantity || 0);
            });

            if (bonomoveAplicable) {
              // Marcar un uso más de este bonomove
              bonomoveUsos[bonomoveAplicable.id] = (bonomoveUsos[bonomoveAplicable.id] || 0) + 1;
              descuentoBono = eventServicio.servicioPrize || eventServicio.servicio?.prize || 0;

              const esBonoPropio = this.bcAplicablesTk?.some(bc => bc.id === bonomoveAplicable.bonocliente?.id);
              if (esBonoPropio) {
                bonoclienteId = bonomoveAplicable.bonocliente.id;
                modoBono = 'selector';
                bonoCodigoManual = '';
              } else {
                bonoclienteId = null;
                modoBono = 'manual';
                bonoCodigoManual = bonomoveAplicable.bonocliente?.codigo?.toString().padStart(6, '0') || '';
              }
            }
          }

          const grupo = this.fb.group({
            servicioId: [eventServicio.servicio?.id],
            nombre: [eventServicio.servicio?.baseServicio?.name],
            precio: [eventServicio.servicioPrize || eventServicio.servicio?.prize],
            cantidad: [1],
            orden: [detalle.orden],
            userId: [detalle.userId, Validators.required],
            financiable: [eventServicio.servicio?.baseServicio?.servicioTipo?.codigoTipo === 'medicinaEstetica'],
            financiado: [detalle.financiado || false],
            numPlazos: [{
              value: detalle.numPlazos,
              disabled: detalle.financiado || false
            }],
            precioPlazo: [detalle.precioPlazo],
            bonoclienteId: [bonoclienteId],
            bonoCodigoManual: [bonoCodigoManual],
            modoBono: [modoBono],
            descuentoBono: [descuentoBono]
          });

          grupo.get('financiado')?.valueChanges.subscribe(financiado => {
            const control = grupo.get('numPlazos');
            financiado ? control?.disable() : control?.enable();
          });

          grupo.get('bonoclienteId')?.valueChanges.subscribe(bonoId => {
            if (!bonoId) {
              // IMPORTANTE: Usar emitEvent: true para que se recalcule el total
              grupo.get('descuentoBono')?.setValue(0);
            }
          });

          grupos.push(grupo);
        }
      });
    } else {
      this.cita.eventServicios.forEach(eventServicio => {
        const detalle = eventServicio.eventServicioDetalles?.[0] ?? new EventServicioDetalle();
        const cantidad = eventServicio.cantidad ?? 1;
        // se guarda lista para tenerla y mostrar bonos para pagar servicios
        this.actualizarBonosParaServicio(eventServicio.servicio?.id);

        // CREAR UN FormGroup POR CADA UNIDAD
        for (let i = 0; i < cantidad; i++) {
          const grupo = this.fb.group({
            servicioId: [eventServicio.servicio?.id],
            nombre: [eventServicio.servicio?.baseServicio.name],
            precio: [eventServicio.servicio?.prize],
            cantidad: [1], // cada unidad por separado
            orden: [detalle.orden ?? null],
            userId: [detalle.userId ?? null, Validators.required],
            financiable: [eventServicio.servicio?.baseServicio.servicioTipo.codigoTipo === 'medicinaEstetica'],
            financiado: [false],
            numPlazos: [{ value: null, disabled: false }],
            precioPlazo: [null],
            bonoclienteId: [null],
            bonoCodigoManual: [''],
            modoBono: ['selector'],
            descuentoBono: [0]
          });

          // Inicializar estado si ya viene financiado
          if (grupo.value.financiado) {
            grupo.get('numPlazos')?.disable();
          }

          // Suscripción reactiva
          grupo.get('financiado')?.valueChanges.subscribe(financiado => {
            const control = grupo.get('numPlazos');
            financiado ? control?.disable() : control?.enable();
          });

          grupo.get('bonoclienteId')?.valueChanges.subscribe(bonoId => {
            if (!bonoId) {
              // IMPORTANTE: Usar emitEvent: true para que se recalcule el total
              grupo.get('descuentoBono')?.setValue(0);
            }
          });

          grupos.push(grupo);
        }
      });
    }
    return grupos;
  }

  private getProductoDetalleGroups(): FormGroup[] {
    return this.cita.eventProductos.map(eventProducto => {
      const eventProductoDetalle = eventProducto.eventProductoDetalles?.[0] ?? new EventProductoDetalle();

      return this.fb.group({
        productoId: [eventProducto.producto?.id],
        nombre: [eventProducto.producto?.baseProducto.name],
        precio: [eventProducto.productoPrize || eventProducto.producto?.prize],
        cantidad: [eventProducto.cantidad],
        orden: [eventProductoDetalle.orden ?? null],
        userId: [eventProductoDetalle.userId ?? null, Validators.required]
      });
    });
  }

  getIva(): Observable<SettingValue> {
    return this.settingService.getSettingValue('iva', this.tiendaId).pipe(
      tap(data => {
        this.iva = +data.value;
      }),
      catchError(error => {
        var errorMessage = <any>error;
        if (errorMessage != null) {
          this.sendToastMsg.emit({ mensajeError: true, mensaje: errorMessage, });
        }
        return throwError(error);
      })
    );
  }

  private getUsuarios(): Observable<DataCombo[]> {
    return this.usuarioService.getUsuariosCombo().pipe(
      tap(data => {
        this.usuariosCombo = data;
      }),
      catchError(error => {
        var errorMessage = <any>error;
        if (errorMessage != null) {
          this.sendToastMsg.emit({ mensajeError: true, mensaje: errorMessage, });
        }
        return throwError(error);
      })
    );
  }

  private getBonoclientesAptosParaTicket(): Observable<Bonocliente[]> {
    const ticketId = this.ticketId || undefined;
    return this.ticketService.getBonoclientesClienteParaTicket(this.clienteId, ticketId).pipe(
      tap(data => {
        this.bcAplicablesTk = data;

        // Si estamos editando, ajustar saldos considerando TODOS los usos del bono en este ticket
        if (this.ticket?.bonomoves) {
          this.bcAplicablesTk = this.bcAplicablesTk.map(bc => {
            // Buscar TODOS los bonomoves que usen este bonocliente en el ticket
            const bonomovesDelBono = this.ticket.bonomoves.filter(bm =>
              bm.bonocliente?.id === bc.id
            );

            if (bonomovesDelBono.length > 0) {
              // Sumar TODAS las cantidades descontadas de este bono en el ticket
              const totalDescontadoEnTicket = bonomovesDelBono.reduce((acc, bm) => {
                return acc + (bm.quantity || 0);
              }, 0);

              return {
                ...bc,
                saldo: bc.saldo + totalDescontadoEnTicket // Devolver todo lo descontado
              };
            }

            return bc;
          });
        }

        this.bcServicios = this.bcAplicablesTk.filter(
          bc => bc.bono?.baseBono?.servicioTipo !== null
        );

        this.bcEconomicos = this.bcAplicablesTk.filter(
          bc => bc.bono?.baseBono?.servicioTipo === null
        );
      }),
      catchError(error => {
        var errorMessage = <any>error;
        if (errorMessage != null) {
          this.sendToastMsg.emit({ mensajeError: true, mensaje: errorMessage, });
        }
        return throwError(error);
      })
    );
  }

  updateToastMsg($event: any) {
    this.sendToastMsg.emit({
      mensajeError: $event.mensajeError,
      mensaje: $event.mensaje,
    });
  }

}
